import {useMemo} from 'react'
import {View} from 'react-native'
import Svg, {G, Path} from 'react-native-svg'
import {Trans, useLingui} from '@lingui/react/macro'
import {useQuery} from '@tanstack/react-query'

import {
  getVerificationDetails,
  type VerificationDetails,
} from '#/lib/wsoc/actor'
import {nationalityName} from '#/locale/helpers'
import {useSession, useWsocAgent} from '#/state/session'
import {atoms as a, useBreakpoints} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'
import type * as bsky from '#/types/bsky'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {type AccountType} from '#/wsocial/types/profile'

export {useDialogControl} from '#/components/Dialog'

function ageBand(birthDateApprox: string, ageLabel: string): string {
  const age = new Date().getFullYear() - new Date(birthDateApprox).getFullYear()
  const lower = Math.floor(age / 10) * 10
  return `${ageLabel} ${lower}-${lower + 10}`
}

// -- component ----------------------------------------------------------------

export function VerificationsWSocialDialog({
  control,
  profile,
  accountType,
}: {
  control: Dialog.DialogControlProps
  profile: bsky.profile.AnyProfileView
  accountType?: AccountType
}) {
  return (
    <Dialog.Outer control={control} nativeOptions={{preventExpansion: true}}>
      <Dialog.Handle />
      <Inner control={control} profile={profile} accountType={accountType} />
    </Dialog.Outer>
  )
}

function Inner({
  control,
  profile,
  accountType: labelAccountType,
}: {
  control: Dialog.DialogControlProps
  profile: bsky.profile.AnyProfileView
  accountType?: AccountType
}) {
  const {t: l} = useLingui()
  const {gtMobile} = useBreakpoints()
  const {currentAccount} = useSession()
  const isViewer = profile.did === currentAccount?.did
  const agent = useWsocAgent()

  const {data, isLoading, isError} = useQuery({
    queryKey: ['verificationDetails', profile.did],
    queryFn: () => agent.account.getVerificationDetails(profile.did),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const dialogLabel = isViewer ? l`Your verification` : l`Account verification`

  return (
    <Dialog.ScrollableInner
      label={dialogLabel}
      style={[
        gtMobile ? {width: 'auto', maxWidth: 399, minWidth: 300} : a.w_full,
      ]}>
      <Content
        data={data}
        isLoading={isLoading}
        isError={isError}
        profile={profile}
        labelAccountType={labelAccountType}
      />
      <View
        style={[
          a.w_full,
          a.gap_sm,
          a.justify_end,
          gtMobile
            ? [a.flex_row, a.flex_row_reverse, a.justify_start]
            : [a.flex_col],
        ]}>
        <Button
          label={l`Close dialog`}
          size="small"
          variant="solid"
          color="primary"
          onPress={() => {
            control.close()
          }}>
          <ButtonText>
            <Trans>Close</Trans>
          </ButtonText>
        </Button>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

function Content({
  data,
  isLoading,
  isError,
  profile,
}: {
  data: VerificationDetails | null | undefined
  isLoading: boolean
  isError: boolean
  profile: bsky.profile.AnyProfileView
  labelAccountType?: AccountType
}) {
  const {i18n, t: l} = useLingui()
  const s = useSemanticTokens()

  const type = data?.accountType
  const displayName = profile.displayName || profile.handle
  const hasWid = !!data?.widVerifiedAt

  const checkItems: string[] = useMemo(() => {
    if (type === 'personal' && hasWid) {
      return [
        [data.givenNames, data.familyNames].filter(Boolean).join(' '),
        data.nationality ? nationalityName(data.nationality, i18n.locale) : '',
        data.birthDateApprox ? ageBand(data.birthDateApprox, l`Age`) : '',
      ].filter(Boolean)
    }
    return []
  }, [data, type, hasWid, l])

  const widLayout = checkItems.length > 0

  const verifiedBy = useMemo(() => {
    if (data) {
      if (widLayout && data.widVerifiedAt) {
        return {
          label: l`W Identity app`,
          date: data.widVerifiedAt,
          showWIdAvatar: true,
          avatar: <WIdentityAvatar />,
        }
      }
      if (data.widVerifiedAt) {
        return {
          label: l`W Identity app`,
          date: data.widVerifiedAt,
          showWIdAvatar: true,
          avatar: <WIdentityAvatar />,
        }
      }
      if (
        (type === 'personal' ||
          type === 'organization' ||
          type === 'service') &&
        data.adminVerifiedAt
      ) {
        return {
          label: l`W Social`,
          date: data.adminVerifiedAt,
          showWIdAvatar: true,
          avatar: <WSocialAvatar />,
        }
      }
    }
    return null
  }, [widLayout, data, checkItems, type, l])

  const verifiedDate = useMemo(() => {
    if (verifiedBy?.date) {
      return i18n.date(new Date(verifiedBy?.date), {dateStyle: 'long'})
    }
    return ''
  }, [verifiedBy, i18n])

  if (isLoading) {
    return (
      <View style={[a.gap_md, a.pb_lg]}>
        <View
          style={[
            {
              height: 28,
              width: '60%',
              borderRadius: 4,
              backgroundColor: s.bg.contrast_25,
            },
          ]}
        />
        <View
          style={[
            {
              height: 16,
              width: '80%',
              borderRadius: 4,
              backgroundColor: s.bg.contrast_25,
            },
          ]}
        />
      </View>
    )
  }

  if (isError || data === null) {
    return (
      <View style={[a.gap_md, a.pb_lg]}>
        <Text style={[a.text_2xl, a.font_semi_bold, a.leading_tight]}>
          <Trans>Something went wrong</Trans>
        </Text>
        <Text style={[a.text_md, a.leading_snug, {color: s.text.medium}]}>
          <Trans>
            There was a problem reading the account type. Please reload.
          </Trans>
        </Text>
      </View>
    )
  }

  const title =
    type === 'personal' && hasWid
      ? l`${displayName} is a verified human`
      : type === 'personal' || type === 'organization'
        ? l`${displayName} is verified`
        : type === 'service'
          ? l`${displayName} is a verified service account`
          : type === 'bot'
            ? l`${displayName} is a robot account`
            : type === 'test'
              ? l`${displayName} is a test user`
              : l`${displayName} is verified`

  const description = hasWid
    ? l`This account has been verified as belonging to a real person using a valid government-issued ID.`
    : type === 'personal'
      ? l`This account has been verified as belonging to a real person by trusted sources.`
      : type === 'organization'
        ? l`This account has been verified as belonging to a company or official organisation using their commonly known name.`
        : type === 'service'
          ? l`This is a W Social service account — typically used for automated feeds, moderation, and similar functions.`
          : type === 'bot'
            ? l`This is a robot account — typically used for automated feeds, posting, and similar functions.`
            : type === 'test'
              ? l`This is a developer account used for testing new functionality for the W Social app. Posts and comments may be nonsense — ignore at will.`
              : null

  if (!data) return null

  return (
    <View style={[a.gap_md, {paddingBottom: 22}]}>
      <Text style={[a.text_xl, a.font_semi_bold, a.leading_tight]}>
        {title}
      </Text>
      {description ? (
        <Text style={[a.text_md, a.leading_snug, {color: s.text.medium}]}>
          {description}
        </Text>
      ) : null}

      {checkItems.length > 0 && (
        <View style={[a.gap_2xs, a.pt_xs]}>
          {checkItems.map(item => (
            <View key={item} style={[a.flex_row, a.align_center, a.gap_xs]}>
              <VerifiedCheckIcon />
              <Text style={[a.text_sm, {color: s.text.medium}]}>{item}</Text>
            </View>
          ))}
        </View>
      )}

      {verifiedBy && (
        <>
          <Text style={[a.text_sm, {color: s.text.medium}]}>
            <Trans>Verified by:</Trans>
          </Text>
          <View style={[a.flex_row, a.align_start, a.gap_sm]}>
            {verifiedBy.showWIdAvatar ? (
              verifiedBy.avatar
            ) : (
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: s.bg.contrast_100,
                  borderWidth: 1,
                  borderColor: s.border.contrast_low,
                }}
              />
            )}
            <View style={[a.gap_2xs, a.justify_center]}>
              <Text style={[a.text_md, a.font_semi_bold]}>
                {verifiedBy.label}
              </Text>
              <Text style={[a.text_xs, {color: s.text.medium}]}>
                {verifiedDate}
              </Text>
            </View>
          </View>
        </>
      )}
    </View>
  )
}

// -- inline SVG icons ---------------------------------------------------------

/*
 * 10×10 filled checkmark using the W Social brand green (#11E8B2).
 * Shape matches the Figma "Verified" icon (node 3028:24199).
 */
function VerifiedCheckIcon() {
  return (
    <Svg width={10} height={10} viewBox="0 0 10 10" fill="none">
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.9475 1.24167C9.05803 1.32233 9.15157 1.42398 9.22281 1.5408C9.29404 1.65763 9.34155 1.78734 9.36264 1.92254C9.38373 2.05773 9.37797 2.19576 9.34571 2.32873C9.31344 2.4617 9.25529 2.58701 9.17458 2.6975L4.91542 8.53083C4.83389 8.64257 4.73092 8.73694 4.61253 8.80846C4.49413 8.87997 4.36268 8.92719 4.22584 8.94736C4.089 8.96753 3.94952 8.96024 3.81553 8.92593C3.68153 8.89162 3.55572 8.83096 3.44542 8.7475L1.0375 6.92375C0.820909 6.7557 0.679295 6.50905 0.643366 6.23727C0.607438 5.9655 0.680092 5.69052 0.845574 5.47196C1.01106 5.2534 1.25602 5.10889 1.52735 5.06975C1.79868 5.03062 2.0745 5.10002 2.295 5.26292L3.8575 6.44625L7.49167 1.46875C7.65457 1.24565 7.89941 1.0964 8.17235 1.05381C8.44529 1.01123 8.72396 1.0788 8.94708 1.24167H8.9475Z"
        fill="#11E8B2"
      />
    </Svg>
  )
}

function WSocialAvatar() {
  return (
    <Svg width={36} height={36} viewBox="0 0 36 36" fill="none">
      {/* Rounded background */}
      <Path
        d="M28.6027 0H7.39726C3.31187 0 0 3.31187 0 7.39726V28.6027C0 32.6881 3.31187 36 7.39726 36H28.6027C32.6881 36 36 32.6881 36 28.6027V7.39726C36 3.31187 32.6881 0 28.6027 0Z"
        fill="#28004E"
      />
      {/* W mark — dark teal layer */}
      <Path
        d="M18.1282 18.6805L22.9167 18.2416L26.6351 5.69589H32.9079L22.9167 29.7123L18.1282 18.6805Z"
        fill="#61A38B"
      />
      {/* W mark — white layer 1 */}
      <Path
        d="M12.4964 6.34685H7.8411L14.7748 22.6011L17.28 18.3403L12.4964 6.34685Z"
        fill="white"
      />
      {/* W mark — dark teal layer 2 */}
      <Path
        d="M19.1441 14.0893L21.4915 13.8723C21.7578 13.8477 21.9797 13.6652 22.0537 13.4088L24.8351 4.21151C24.9189 3.93534 25.1704 3.74795 25.4564 3.74795H28.8148C29.2784 3.74795 29.594 4.22137 29.4164 4.64548L22.0932 22.2362C21.9107 22.68 21.5803 22.6405 21.0871 22.6405L18.6016 14.9918C18.4241 14.5874 18.6953 14.1288 19.1392 14.0844L19.1441 14.0893Z"
        fill="#61A38B"
      />
      {/* W mark — white layer 2 */}
      <Path
        d="M15.909 10.4844L17.3392 8.21589C17.8915 7.33808 19.2181 7.51562 19.5238 8.50685L21.0921 13.6208L23.2077 6.34685H27.7101L21.097 22.6455L15.914 10.4844H15.909Z"
        fill="white"
      />
      {/* W mark — brand green accent */}
      <Path
        d="M17.4132 13.966L18.5425 12.1759C19.0997 11.2932 20.4312 11.4756 20.737 12.4668L21.0921 13.6208L23.9474 3.74795H28.7655L21.0871 22.6405L17.4082 13.9611L17.4132 13.966Z"
        fill="#1DF2BA"
      />
      {/* "ID" badge at bottom-right (translate 15.05, 24.21) */}
      {/*<G transform="translate(15.05, 24.21)">
                <Path
                    d="M5.06501 7.53176H2.74076V0H5.16799C5.89861 0 6.5238 0.150782 7.04357 0.452347C7.56334 0.751459 7.96175 1.18174 8.2388 1.74319C8.51584 2.30219 8.65437 2.97151 8.65437 3.75117C8.65437 4.53573 8.51462 5.21118 8.23512 5.77753C7.95562 6.34143 7.54863 6.77539 7.01415 7.07941C6.47967 7.38097 5.82996 7.53176 5.06501 7.53176ZM3.65281 6.72268H5.00617C5.62892 6.72268 6.14501 6.60255 6.55445 6.36227C6.96389 6.122 7.26913 5.77999 7.47017 5.33622C7.67122 4.89245 7.77174 4.3641 7.77174 3.75117C7.77174 3.14314 7.67244 2.61969 7.47385 2.18083C7.27526 1.73951 6.9786 1.40117 6.58387 1.1658C6.18914 0.927985 5.69756 0.809075 5.10915 0.809075H3.65281V6.72268Z"
                    fill="white"
                />
                <Path d="M0.912068 0V7.53176H1.97887e-05V0H0.912068Z" fill="white" />
            </G>*/}
    </Svg>
  )
}

/*
 * 36×36 W Identity app avatar.
 * Composed from the Figma avatar (node 3028:24210): dark rounded square with
 * the W Social mark, and the "ID" badge overlay at translate(15.05, 24.21).
 */
function WIdentityAvatar() {
  return (
    <Svg width={36} height={36} viewBox="0 0 36 36" fill="none">
      {/* Rounded background */}
      <Path
        d="M28.6027 0H7.39726C3.31187 0 0 3.31187 0 7.39726V28.6027C0 32.6881 3.31187 36 7.39726 36H28.6027C32.6881 36 36 32.6881 36 28.6027V7.39726C36 3.31187 32.6881 0 28.6027 0Z"
        fill="#28004E"
      />
      {/* W mark — dark teal layer */}
      <Path
        d="M18.1282 18.6805L22.9167 18.2416L26.6351 5.69589H32.9079L22.9167 29.7123L18.1282 18.6805Z"
        fill="#61A38B"
      />
      {/* W mark — white layer 1 */}
      <Path
        d="M12.4964 6.34685H7.8411L14.7748 22.6011L17.28 18.3403L12.4964 6.34685Z"
        fill="white"
      />
      {/* W mark — dark teal layer 2 */}
      <Path
        d="M19.1441 14.0893L21.4915 13.8723C21.7578 13.8477 21.9797 13.6652 22.0537 13.4088L24.8351 4.21151C24.9189 3.93534 25.1704 3.74795 25.4564 3.74795H28.8148C29.2784 3.74795 29.594 4.22137 29.4164 4.64548L22.0932 22.2362C21.9107 22.68 21.5803 22.6405 21.0871 22.6405L18.6016 14.9918C18.4241 14.5874 18.6953 14.1288 19.1392 14.0844L19.1441 14.0893Z"
        fill="#61A38B"
      />
      {/* W mark — white layer 2 */}
      <Path
        d="M15.909 10.4844L17.3392 8.21589C17.8915 7.33808 19.2181 7.51562 19.5238 8.50685L21.0921 13.6208L23.2077 6.34685H27.7101L21.097 22.6455L15.914 10.4844H15.909Z"
        fill="white"
      />
      {/* W mark — brand green accent */}
      <Path
        d="M17.4132 13.966L18.5425 12.1759C19.0997 11.2932 20.4312 11.4756 20.737 12.4668L21.0921 13.6208L23.9474 3.74795H28.7655L21.0871 22.6405L17.4082 13.9611L17.4132 13.966Z"
        fill="#1DF2BA"
      />
      {/* "ID" badge at bottom-right (translate 15.05, 24.21) */}
      <G transform="translate(15.05, 24.21)">
        <Path
          d="M5.06501 7.53176H2.74076V0H5.16799C5.89861 0 6.5238 0.150782 7.04357 0.452347C7.56334 0.751459 7.96175 1.18174 8.2388 1.74319C8.51584 2.30219 8.65437 2.97151 8.65437 3.75117C8.65437 4.53573 8.51462 5.21118 8.23512 5.77753C7.95562 6.34143 7.54863 6.77539 7.01415 7.07941C6.47967 7.38097 5.82996 7.53176 5.06501 7.53176ZM3.65281 6.72268H5.00617C5.62892 6.72268 6.14501 6.60255 6.55445 6.36227C6.96389 6.122 7.26913 5.77999 7.47017 5.33622C7.67122 4.89245 7.77174 4.3641 7.77174 3.75117C7.77174 3.14314 7.67244 2.61969 7.47385 2.18083C7.27526 1.73951 6.9786 1.40117 6.58387 1.1658C6.18914 0.927985 5.69756 0.809075 5.10915 0.809075H3.65281V6.72268Z"
          fill="white"
        />
        <Path d="M0.912068 0V7.53176H1.97887e-05V0H0.912068Z" fill="white" />
      </G>
    </Svg>
  )
}
